import React, { FC } from 'react'
import voca from 'voca'

import { PrepCheckDonePageTemplate } from '../components'
import { PageProps } from '../lib/entities'
import { useAppDispatch, useAppSelector } from '../lib/store'
import {
    LanguageCode,
    LoggedPage,
    NativeLanguageDTO,
    PrepCheckResultOverview,
    PrepCheckUpNextDTO,
    getLangObjects,
} from '@hazadapt-git/public-core-base'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    getOnePrepCheckResultOverview,
    getUpNextPrepCheck,
    logEvent,
    toast,
    getGuestPrepCheckResults,
    getGuestPrepCheckSubmission,
    removeOneGuestPrepCheck,
    signup,
    submitGuestPrepCheck,
    getDefaultGuestStats,
} from '../lib/utils'
import {
    getAllPrepChecksThunk,
    getPrepCheckUserStatsThunk,
    selectActivePrepCheck,
    selectIsAppReady,
    selectLanguage,
    selectPrepChecks,
    selectPrepCheckUserStats,
    selectSupportedLanguages,
    selectUser,
    switchLanguageThunk,
} from '../lib/slices'
import { IoCloudDownloadOutline, IoLanguage } from 'react-icons/io5'
import {
    successColor,
    primaryIconSize,
    errorColor,
} from '../lib/styles/universal'
import { shallowEqual } from 'react-redux'
import { getEnvVars } from '../lib/config'

interface PrepCheckDonePageProps extends PageProps {}

const env = getEnvVars()
export const PrepCheckDonePage: FC<PrepCheckDonePageProps> = (
    props: PrepCheckDonePageProps
) => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useAppDispatch()

    const language = useAppSelector(selectLanguage)
    const user = useAppSelector(selectUser, shallowEqual)
    const appReady = useAppSelector(selectIsAppReady)
    const prepCheckStats = useAppSelector(
        selectPrepCheckUserStats,
        shallowEqual
    )
    const activePrepCheck = useAppSelector(selectActivePrepCheck, shallowEqual)
    const supportedLanguages = useAppSelector(
        selectSupportedLanguages,
        shallowEqual
    )
    const prepChecks = useAppSelector(selectPrepChecks, shallowEqual)

    const [upNext, setUpNext] = React.useState<PrepCheckUpNextDTO>()
    const [results, setResults] = React.useState<PrepCheckResultOverview>()

    const [userStatsRetrieved, setUserStatsRetrieved] =
        React.useState<boolean>(false)
    const [resultRetrieved, setResultRetrieved] = React.useState<boolean>(false)
    const [upNextRetrieved, setUpNextRetrieved] = React.useState<boolean>(false)
    const [languageSelectorModalOpen, setLanguageSelectorModalOpen] =
        React.useState<boolean>(false)

    React.useEffect(() => {
        document.title = 'Prep Check Complete - HazAdapt'
    }, [])

    React.useEffect(() => {
        if (!appReady) return

        const urlPieces = location.pathname.split('/').slice(1)
        const identifier = urlPieces.at(-2)
        if (identifier && voca.isNumeric(identifier)) {
            const prepCheckId = parseInt(identifier)

            logEvent('OPEN_PAGE', {
                page: LoggedPage.PREP_CHECK_DONE,
                language,
                prep_check_id: prepCheckId,
            })

            if (user) {
                // Authenticated users: fetch from API
                getOnePrepCheckResultOverview(prepCheckId, language)
                    .then((res) => {
                        setResultRetrieved(true)
                        setResults(res)
                    })
                    .catch((err) => {
                        console.error(err)
                        navigate('/prep-checks', { replace: true })
                    })

                // Additional data only for authenticated users
                dispatch(getPrepCheckUserStatsThunk(language)).then(() => {
                    setUserStatsRetrieved(true)
                })
                getUpNextPrepCheck(prepCheckId)
                    .then(setUpNext)
                    .catch(console.error)
                    .finally(() => {
                        setUpNextRetrieved(true)
                    })
            } else {
                // Guests: load from localStorage
                const localResults = getGuestPrepCheckResults(prepCheckId)
                if (localResults) {
                    setResultRetrieved(true)
                    setResults(localResults)
                    // No user stats for guests
                    setUserStatsRetrieved(true)
                    const currentIndex = prepChecks.findIndex(
                        (check) => check.id === prepCheckId
                    )
                    if (currentIndex !== -1 && prepChecks.length > 1) {
                        // If last, wrap to first; else next
                        const nextIndex =
                            currentIndex < prepChecks.length - 1
                                ? currentIndex + 1
                                : 0
                        const nextCheck = prepChecks[nextIndex]
                        setUpNext({
                            id: nextCheck.id,
                            name: nextCheck.title,
                            icon: nextCheck.dark_icon || {
                                src: '',
                                url: '',
                                alt: '',
                            },
                        })
                    }
                    setUpNextRetrieved(true)
                } else {
                    // No results found, redirect to landing page
                    navigate('/prep-checks', { replace: true })
                }
            }
        } else {
            navigate('/prep-checks', { replace: true })
        }
    }, [
        dispatch,
        language,
        location.pathname,
        navigate,
        appReady,
        user,
        prepChecks,
    ])

    React.useEffect(() => {
        if (!activePrepCheck) {
            navigate('/prep-checks', { replace: true })
        }
    }, [activePrepCheck, navigate])

    const onRetakePress: React.MouseEventHandler = (e) => {
        e.preventDefault()
        e.stopPropagation()

        if (!activePrepCheck) return
        if (!user) removeOneGuestPrepCheck(activePrepCheck.id)
        navigate(
            `/prep-checks/${activePrepCheck.id}?from=${location.pathname}`,
            { state: { retake: true } }
        )
    }

    const onUpNextPress: React.MouseEventHandler = (e) => {
        if (!upNext) return
        navigate(`/prep-checks/${upNext.id}?from=${location.pathname}`)
    }

    const onViewResultsPress: React.MouseEventHandler = (e) => {
        if (!activePrepCheck) return
        navigate(`/prep-checks/${activePrepCheck.id}/results`, {
            state: { allowBack: true },
        })
    }

    const onSignUpClick = async () => {
        if (!activePrepCheck) return
        const redirectUrl = `${env.frontendUriBase}/prep-checks/${activePrepCheck.id}/complete`
        await signup(redirectUrl)
    }

    const onJourneyCompletionClick: React.MouseEventHandler = (e) => {
        navigate('/prep-checks')
    }

    const onMyEmergencyItemsClick: React.MouseEventHandler = (e) => {
        navigate('/my-emergency-items')
    }

    const onLanguageChange = async (lang: LanguageCode) => {
        const langObject: NativeLanguageDTO = getLangObjects([lang])[0]
        setLanguageSelectorModalOpen(false)
        toast(
            `Downloading results in ${langObject.title}.`,
            <IoCloudDownloadOutline
                color={successColor}
                size={primaryIconSize}
            />
        )
        try {
            await dispatch(switchLanguageThunk({ lang }))
            dispatch(getPrepCheckUserStatsThunk(lang))
            await dispatch(getAllPrepChecksThunk(lang))

            // For guests, resubmit the stored submission to get results in the new language
            if (!user && activePrepCheck) {
                const storedSubmission = getGuestPrepCheckSubmission(
                    activePrepCheck.id
                )
                if (storedSubmission) {
                    const { results } = await submitGuestPrepCheck(
                        storedSubmission
                    )
                    setResults(results)
                }
            }

            toast(
                `Results have been translated into ${langObject.title}.`,
                <IoLanguage color={successColor} size={primaryIconSize} />
            )
        } catch (err) {
            console.error(err)
            toast(
                `Unable to download results in ${langObject.title}.`,
                <IoLanguage color={errorColor} size={primaryIconSize} />
            )
        }
    }

    const onTranslateClick: React.MouseEventHandler = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setLanguageSelectorModalOpen(true)
    }

    const onLanguageSelectorModalClose = () => {
        setLanguageSelectorModalOpen(false)
    }

    const displayStats = user
        ? prepCheckStats
        : getDefaultGuestStats('prep-check-done')

    return activePrepCheck && results ? (
        <PrepCheckDonePageTemplate
            journeyPhaseIcon={displayStats.journeyPhaseIcon}
            journeyPhaseName={displayStats.journeyPhase}
            journeyPhaseSubcopy={displayStats.journeyPhaseSubcopy}
            prepChecksCompleted={displayStats.numCompleted}
            totalPrepChecks={displayStats?.numTotal || 0}
            prepCheckName={
                activePrepCheck.title.includes('Prep Check')
                    ? activePrepCheck.title
                    : `${activePrepCheck.title} Prep Check`
            }
            badgeName={results.badgeName}
            badge={results.badge}
            score={results.score}
            upNext={upNext}
            onUpNextPress={onUpNextPress}
            onViewResultsPress={onViewResultsPress}
            onRetakePress={onRetakePress}
            onJourneyCompletionClick={onJourneyCompletionClick}
            onMyEmergencyItemsClick={onMyEmergencyItemsClick}
            newBadge={results.newBadge}
            badgeAwardText={results.badgeAwardText}
            newJourneyPhase={results.newJourneyPhase}
            newJourneyPhaseAwardText={results.newJourneyPhaseAwardText}
            loggedIn={!!user}
            loading={
                props.loading ||
                !userStatsRetrieved ||
                !resultRetrieved ||
                !upNextRetrieved
            }
            languages={supportedLanguages}
            selectedLanguage={language}
            onLanguageChange={onLanguageChange}
            onLanguageSelectorModalClose={onLanguageSelectorModalClose}
            languageSelectorModalOpen={languageSelectorModalOpen}
            onTranslateClick={onTranslateClick}
            onSignUpClick={onSignUpClick}
        />
    ) : null
}
